import tourismEmissionFactors from '../data/tourism_emission_factors.json';

/**
 * Otel ve turizm sektörü için karbon ayak izi hesaplama motoru
 * Bu dosya otel operasyonlarının karbon emisyonlarını hesaplamak için kullanılır
 */

class TourismCarbonCalculator {
  constructor() {
    this.emissionFactors = tourismEmissionFactors;
  }

  /**
   * Konaklama emisyonlarını hesaplar
   * @param {Object} data - {roomType, nights, rooms}
   * @returns {number} kg CO2e
   */
  calculateAccommodationEmissions(data) {
    const { roomType = 'standard', nights = 1, rooms = 1 } = data;
    const factor = this.emissionFactors.accommodation.roomNight[roomType] || 
                  this.emissionFactors.accommodation.roomNight.standard;
    
    return factor * nights * rooms;
  }

  /**
   * Enerji tüketimi emisyonlarını hesaplar
   * @param {Object} data - {electricity, heating, energyType}
   * @returns {number} kg CO2e
   */
  calculateEnergyEmissions(data) {
    const { electricity = 0, heating = 0, energyType = 'grid', heatingType = 'naturalGas' } = data;
    
    const electricityFactor = this.emissionFactors.energy.electricity[energyType] || 
                             this.emissionFactors.energy.electricity.grid;
    const heatingFactor = this.emissionFactors.energy.heating[heatingType] || 
                         this.emissionFactors.energy.heating.naturalGas;

    return (electricity * electricityFactor) + (heating * heatingFactor);
  }

  /**
   * Su tüketimi emisyonlarını hesaplar
   * @param {Object} data - {waterConsumption, waterType}
   * @returns {number} kg CO2e
   */
  calculateWaterEmissions(data) {
    const { waterConsumption = 0, waterType = 'municipal' } = data;
    const factor = this.emissionFactors.water[waterType] || 
                  this.emissionFactors.water.municipal;
    
    return waterConsumption * factor;
  }

  /**
   * Yemek ve içecek emisyonlarını hesaplar
   * @param {Object} data - {breakfast, lunch, dinner, beverages, guests}
   * @returns {number} kg CO2e
   */
  calculateFoodEmissions(data) {
    const { 
      breakfast = { type: 'continental', count: 0 },
      lunch = { type: 'standard', count: 0 },
      dinner = { type: 'standard', count: 0 },
      beverages = { alcoholic: 0, nonAlcoholic: 0, coffee: 0, tea: 0 },
      guests = 1
    } = data;

    let totalEmissions = 0;

    // Kahvaltı
    const breakfastFactor = this.emissionFactors.food.breakfast[breakfast.type] || 
                           this.emissionFactors.food.breakfast.continental;
    totalEmissions += breakfastFactor * breakfast.count * guests;

    // Öğle yemeği
    const lunchFactor = this.emissionFactors.food.lunch[lunch.type] || 
                       this.emissionFactors.food.lunch.standard;
    totalEmissions += lunchFactor * lunch.count * guests;

    // Akşam yemeği
    const dinnerFactor = this.emissionFactors.food.dinner[dinner.type] || 
                        this.emissionFactors.food.dinner.standard;
    totalEmissions += dinnerFactor * dinner.count * guests;

    // İçecekler
    Object.keys(beverages).forEach(beverage => {
      const factor = this.emissionFactors.food.beverages[beverage] || 0;
      totalEmissions += factor * beverages[beverage] * guests;
    });

    return totalEmissions;
  }

  /**
   * Çamaşır ve temizlik emisyonlarını hesaplar
   * @param {Object} data - {towels, bedLinen, clothing, tableLinen, uniform}
   * @returns {number} kg CO2e
   */
  calculateLaundryEmissions(data) {
    const { 
      towels = 0, 
      bedLinen = 0, 
      clothing = 0, 
      tableLinen = 0, 
      uniform = 0 
    } = data;

    let totalEmissions = 0;

    Object.keys(data).forEach(item => {
      const factor = this.emissionFactors.laundry[item] || 0;
      totalEmissions += factor * data[item];
    });

    return totalEmissions;
  }

  /**
   * Atık yönetimi emisyonlarını hesaplar
   * @param {Object} data - {organic, paper, plastic, glass, metal, mixed, hazardous}
   * @returns {number} kg CO2e
   */
  calculateWasteEmissions(data) {
    const { 
      organic = 0, 
      paper = 0, 
      plastic = 0, 
      glass = 0, 
      metal = 0, 
      mixed = 0, 
      hazardous = 0 
    } = data;

    let totalEmissions = 0;

    Object.keys(data).forEach(wasteType => {
      const factor = this.emissionFactors.waste[wasteType] || 0;
      totalEmissions += factor * data[wasteType];
    });

    return totalEmissions;
  }

  /**
   * Ulaşım emisyonlarını hesaplar
   * @param {Object} data - {staff, guest, delivery}
   * @returns {number} kg CO2e
   */
  calculateTransportationEmissions(data) {
    const { staff = {}, guest = {}, delivery = {} } = data;
    let totalEmissions = 0;

    // Personel ulaşımı
    Object.keys(staff).forEach(transport => {
      const factor = this.emissionFactors.transportation.staff[transport] || 0;
      totalEmissions += factor * (staff[transport] || 0);
    });

    // Misafir ulaşımı
    Object.keys(guest).forEach(transport => {
      const factor = this.emissionFactors.transportation.guest[transport] || 0;
      totalEmissions += factor * (guest[transport] || 0);
    });

    // Teslimat ulaşımı
    Object.keys(delivery).forEach(transport => {
      const factor = this.emissionFactors.transportation.delivery[transport] || 0;
      totalEmissions += factor * (delivery[transport] || 0);
    });

    return totalEmissions;
  }

  /**
   * Tesis hizmetleri emisyonlarını hesaplar
   * @param {Object} data - {spa, recreation, conference}
   * @returns {number} kg CO2e
   */
  calculateFacilitiesEmissions(data) {
    const { spa = {}, recreation = {}, conference = {} } = data;
    let totalEmissions = 0;

    // Spa hizmetleri
    Object.keys(spa).forEach(service => {
      const factor = this.emissionFactors.facilities.spa[service] || 0;
      totalEmissions += factor * (spa[service] || 0);
    });

    // Rekreasyon hizmetleri
    Object.keys(recreation).forEach(service => {
      const factor = this.emissionFactors.facilities.recreation[service] || 0;
      totalEmissions += factor * (recreation[service] || 0);
    });

    // Konferans hizmetleri
    Object.keys(conference).forEach(service => {
      const factor = this.emissionFactors.facilities.conference[service] || 0;
      totalEmissions += factor * (conference[service] || 0);
    });

    return totalEmissions;
  }

  /**
   * Mevsimsel düzeltme faktörünü uygular
   * @param {number} emissions - Ham emisyon değeri
   * @param {string} season - Mevsim (summer, winter, spring, autumn)
   * @param {string} category - Kategori (cooling, heating, lighting)
   * @returns {number} Düzeltilmiş emisyon değeri
   */
  applySeasonalFactor(emissions, season = 'spring', category = 'heating') {
    const factor = this.emissionFactors.seasonalFactors[season]?.[category] || 1.0;
    return emissions * factor;
  }

  /**
   * Toplam karbon ayak izini hesaplar
   * @param {Object} inputData - Tüm girdi verileri
   * @returns {Object} Detaylı emisyon raporu
   */
  calculateTotalFootprint(inputData) {
    const {
      accommodation = {},
      energy = {},
      water = {},
      food = {},
      laundry = {},
      waste = {},
      transportation = {},
      facilities = {},
      period = 'daily',
      season = 'spring'
    } = inputData;

    // Her kategori için emisyonları hesapla
    const emissions = {
      accommodation: this.calculateAccommodationEmissions(accommodation),
      energy: this.calculateEnergyEmissions(energy),
      water: this.calculateWaterEmissions(water),
      food: this.calculateFoodEmissions(food),
      laundry: this.calculateLaundryEmissions(laundry),
      waste: this.calculateWasteEmissions(waste),
      transportation: this.calculateTransportationEmissions(transportation),
      facilities: this.calculateFacilitiesEmissions(facilities)
    };

    // Mevsimsel düzeltme uygula
    emissions.energy = this.applySeasonalFactor(emissions.energy, season, 'heating');

    // Toplam hesapla
    const total = Object.values(emissions).reduce((sum, value) => sum + value, 0);

    // Periyot düzeltmesi
    const periodMultiplier = this.getPeriodMultiplier(period);
    const adjustedTotal = total * periodMultiplier;

    return {
      breakdown: emissions,
      total: adjustedTotal,
      period,
      season,
      timestamp: new Date().toISOString(),
      perRoomNight: accommodation.rooms && accommodation.nights ? 
        adjustedTotal / (accommodation.rooms * accommodation.nights) : 0,
      recommendations: this.generateRecommendations(emissions, adjustedTotal)
    };
  }

  /**
   * Periyot çarpanını döndürür
   * @param {string} period - daily, monthly, yearly
   * @returns {number} Çarpan değeri
   */
  getPeriodMultiplier(period) {
    const multipliers = {
      daily: 1,
      monthly: 30,
      yearly: 365
    };
    return multipliers[period] || 1;
  }

  /**
   * Emisyon verilerine göre öneriler üretir
   * @param {Object} emissions - Kategori bazında emisyonlar
   * @param {number} total - Toplam emisyon
   * @returns {Array} Öneriler listesi
   */
  generateRecommendations(emissions, total) {
    const recommendations = [];
    
    // En yüksek emisyon kategorilerini tespit et
    const sortedEmissions = Object.entries(emissions)
      .sort(([,a], [,b]) => b - a)
      .slice(0, 3);

    sortedEmissions.forEach(([category, value]) => {
      const percentage = (value / total * 100).toFixed(1);
      
      switch(category) {
        case 'energy':
          recommendations.push({
            category: 'Enerji',
            priority: 'Yüksek',
            suggestion: `Enerji tüketimi toplam emisyonun %${percentage}'ini oluşturuyor. LED aydınlatma, enerji verimli klimalar ve yenilenebilir enerji kullanımını artırın.`,
            potentialSaving: Math.round(value * 0.3)
          });
          break;
        case 'food':
          recommendations.push({
            category: 'Yemek & İçecek',
            priority: 'Orta',
            suggestion: `F&B emlisyonları %${percentage} oranında. Yerel ve organik ürünler, vegetaryen menü seçenekleri artırılabilir.`,
            potentialSaving: Math.round(value * 0.25)
          });
          break;
        case 'transportation':
          recommendations.push({
            category: 'Ulaşım',
            priority: 'Orta',
            suggestion: `Ulaşım emisyonları %${percentage} seviyesinde. Toplu taşıma teşviki, elektrikli servis araçları kullanımı önerilebilir.`,
            potentialSaving: Math.round(value * 0.4)
          });
          break;
        default:
          recommendations.push({
            category: category.charAt(0).toUpperCase() + category.slice(1),
            priority: 'Düşük',
            suggestion: `${category} kategorisinde iyileştirmeler yapılabilir.`,
            potentialSaving: Math.round(value * 0.2)
          });
      }
    });

    return recommendations;
  }

  /**
   * Sertifikasyon karşılaştırması yapar
   * @param {number} roomNightEmission - Oda-gece başına emisyon
   * @returns {Object} Sertifikasyon durumu
   */
  getCertificationStatus(roomNightEmission) {
    const benchmarks = this.emissionFactors.certificationBenchmarks;
    
    let status = 'Henüz sertifikasyon seviyesinde değil';
    let recommendations = [];

    // Green Key kontrol
    if (roomNightEmission <= benchmarks.greenKey.platinum) {
      status = 'Green Key Platinum seviyesinde';
    } else if (roomNightEmission <= benchmarks.greenKey.gold) {
      status = 'Green Key Gold seviyesinde';
    } else if (roomNightEmission <= benchmarks.greenKey.silver) {
      status = 'Green Key Silver seviyesinde';
    }

    // LEED kontrol
    if (roomNightEmission <= benchmarks.leed.platinum) {
      status += ' - LEED Platinum uyumlu';
    } else if (roomNightEmission <= benchmarks.leed.gold) {
      status += ' - LEED Gold uyumlu';
    }

    // Öneriler
    if (roomNightEmission > benchmarks.greenKey.silver) {
      recommendations.push('Green Key sertifikasyonu için emisyonları azaltın');
    }
    if (roomNightEmission > benchmarks.iso14001.basic) {
      recommendations.push('ISO 14001 çevre yönetim sistemi implementasyonu');
    }

    return {
      status,
      recommendations,
      roomNightEmission,
      benchmarks
    };
  }
}

export default TourismCarbonCalculator;
